import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import DestructorTree from "../DestructorTree";
import { registrationInitialized, getRegistrationStatus } from "../state/registerClientState";
import { Pending, Failed } from "../Status";
import { ScalarProvider, WeakCache, createVariable, createExpressionBuilder } from "../../../../../stream";
import { toStream, toVariable } from "../toStream";
import { is_updating, stop_updating, notifyEntityUpdate } from "../widgets/instructions/update";
import { setModified } from "../state/SaveModifiedStatus";
import { setStickyTextValue } from "../state/mapCommandParameters";
import { isPasswordProperty } from "../state/password";
import { iCollection } from "../state/Collection";
import { nodeEditMode } from "../state/editMode";
import { ordered_graphs, iGraph } from "../state/Graph";
import { iReference, ReferenceInterface } from "../state/Reference";
import ClientStateObjectProxy from "./ClientStateObjectProxy";

export function getValue(text_property: api_cs.Ctext__type__property): string | undefined {
	switch (text_property.properties.is_set.state.name) {
		case "no":
			return undefined;
		case "yes":
			return text_property.properties.is_set.state.node.properties.text;
	}
}

/**@deprecated*/
export async function subscribeToValue<R>(
	text_property: api_cs.Ctext__type__property,
	destructor: DestructorTree,
	callback: (value: string | undefined, destructor: DestructorTree) => R
): Promise<R> {
	await registrationInitialized(text_property);
	let value: string | undefined | null = null;
	let child_destructor = destructor.addChild();
	const on_value = (new_value: string | undefined) => {
		if (value !== null && value !== new_value) {
			child_destructor.destroy();
			child_destructor = destructor.addChild();
			callback(new_value, child_destructor);
		}
		value = new_value;
	};
	destructor.subscribeToStategroup(text_property.properties.is_set, (destructor) => {
		switch (text_property.properties.is_set.state.name) {
			case "yes": {
				const $ = text_property.properties.is_set.state.node;
				return destructor.subscribeToText($, "subscribe_text", on_value);
			}
			case "no":
				return on_value(undefined);
			default:
				return unexpectedState(text_property.properties.is_set.state);
		}
	});
	return callback(getValue(text_property), child_destructor);
}

export function getBackendValue(text_property: api_cs.Ctext__type__property): string | undefined {
	switch (text_property.properties.backend_known.state.name) {
		case "yes":
			return text_property.properties.backend_known.state.node.properties.value;
		case "no":
			return undefined;
	}
}

export const value = WeakCache<api_cs.Ctext__type__property, ScalarProvider<string | Failed>>((property) =>
	createVariable(
		toStream(property.properties.is_set)
			.stateSwitch({
				no: () => Failed as string | Failed,
				yes: ($) => toVariable($, "text"),
			})
			.pipe((v) => v)(),
		false
	)
);

export const valueModified = WeakCache<api_cs.Ctext__type__property, ScalarProvider<boolean>>((property) =>
	createVariable(
		toStream(property.properties.is_editable)
			.stateSwitch({
				no: () => false,
				yes: () =>
					toStream(property.properties.backend_known).stateSwitch({
						no: () =>
							toStream(property.properties.is_set).stateSwitch({
								no: () => false,
								yes: (is_set) =>
									toStream(is_set.properties.by).stateSwitch({
										engine: () => false,
										user: () => true,
									})(),
							})(),
						yes: (backend_known) =>
							toStream(property.properties.is_set).stateSwitch({
								no: () => false,
								yes: (is_set) =>
									toStream(is_set, "text")
										.and(toVariable(backend_known, "value"))
										.pipe(([value, backend_value]) => value !== backend_value)(),
							})(),
					})(),
			})
			.pipe((v) => v)(),
		false
	)
);

export const synchronized = WeakCache<api_cs.Ctext__type__property, ScalarProvider<"no" | "pending" | "yes">>(
	(property) =>
		createVariable(
			createExpressionBuilder(getRegistrationStatus(property)).pipe((status) => {
				switch (status) {
					case "unsubscribed":
					case "pending":
						return "pending" as "no" | "pending" | "yes";
					case "failed":
						return "no";
					case "not subscribable":
						return "no";
					case "subscribed":
						return createExpressionBuilder(valueModified(property)).pipe((modified) => (modified ? "no" : "yes"))();
				}
			})(),
			false
		)
);

export const backendValue = WeakCache<api_cs.Ctext__type__property, ScalarProvider<string | Pending | Failed>>(
	(property) =>
		createVariable(
			toStream(property.properties.backend_known)
				.stateSwitch({
					no: () => Pending as string | Pending | Failed,
					yes: ($) => toVariable($, "value"),
				})
				.pipe((v) => v)(),
			false
		)
);

class TextInterface extends ClientStateObjectProxy {
	modified: ScalarProvider<boolean>;
	graph: iGraph | undefined;
	entry: api_cs.Centries__collection | undefined;
	references_to_me: Set<ReferenceInterface> | undefined;
	reference: ReferenceInterface | undefined;

	constructor(readonly property: api_cs.Ctext__type__property) {
		super();
		this.modified = createVariable(
			toStream(property.properties.is_editable).stateSwitch({
				yes: ($) =>
					toStream($.properties.modified).stateSwitch({
						no: () => false,
						yes: () => true,
					})(),
				no: () => false,
			})(),
			false
		);
		this.graph = property.component_root.input.entity().switch({
			root: () => undefined,
			entry: (entry) => {
				this.entry = entry;
				for (const graph of ordered_graphs(entry.parent)) {
					if (property.inferences.text_type() === graph.edge_reference) {
						return graph;
					}
				}
				return undefined;
			},
		});
		switch (property.properties.navigable.state.name) {
			case "no":
				break;
			case "yes":
				this.reference = iReference(property.properties.navigable.state.node);
		}
	}

	*_graphs_for_which_is_key(): IterableIterator<iGraph> {
		const is_key = this.property.properties.is_key.state;
		switch (is_key.name) {
			case "no":
				break;
			case "yes":
				for (const graph of iCollection(is_key.node.inferences.entry().parent).graphs) {
					yield graph;
				}
		}
	}

	__ensure(value: string) {
		const is_set = this.property.properties.is_set.state;
		switch (is_set.name) {
			case "no":
				break;
			case "yes":
				if (is_set.node.properties.text === value) return;
				break;
		}
		this.__update(value);
	}

	__update(value: string, by?: "user" | "engine") {
		const modified = this.property.properties.backend_known.switch({
			no: () => true,
			yes: ($) => $.properties.value !== value,
		});
		this.property.root.transact(() => {
			this.property.properties.is_editable.switch({
				no: undefined,
				yes: ($) => {
					if (modified) {
						const is_modified = $.properties.modified.ensure("yes", {
							value: value,
							saving: ["no", {}],
						});
						setModified(is_modified);
						if (is_modified.properties.value !== value) {
							is_modified.set_value(value);
						}
					} else {
						$.properties.modified.ensure("no", {});
					}
				},
			});
			const is_set = this.property.properties.is_set.ensure("yes", {
				text: value,
				by: by ?? "engine",
			});
			if (is_set.properties.text !== value) {
				is_set.set_text(value);
			}
			if (by !== undefined) {
				is_set.properties.by.ensure(by, {});
			}
			switch (is_set.properties.by.state.name) {
				case "engine":
					break;
				case "user":
					setStickyTextValue(this.property.inferences.text_type(), value);
			}
		});
		notifyEntityUpdate(this.property.parent);
	}

	update(value: string, by: "user" | "engine", update_graphs = true) {
		if (!is_updating(this.property.parent)) {
			if (this.references_to_me) {
				for (const reference of this.references_to_me) {
					if (reference.propagateKeyUpdates) {
						iText(reference.reference.parent).__update(value, by);
					}
				}
			}
			this.__update(value, by);
			if (update_graphs && this.graph) {
				const node = this.graph.getNode(this.entry!)!;
				switch (node.edge_state.properties.is_selected.state.name) {
					case "no":
						break;
					case "yes":
						this.graph.insertBeforeKey(this.entry!, value);
						break;
				}
			}
			if (this.reference) {
				this.reference.discard();
			}
			stop_updating(this.property.parent);
		}
	}

	discard() {
		this.property.properties.is_set.switch({
			no: undefined,
			yes: () => {
				this.property.properties.is_editable.switch({
					no: undefined,
					yes: ($) => {
						this.property.properties.backend_known.switch({
							no: () => {
								$.properties.modified.ensure("no", {});
								this.property.properties.navigable.switch({
									yes: () => {
										this.property.properties.is_set.ensure("no", {});
									},
									no: () => {
										const is_set = this.property.properties.is_set.ensure("yes", {
											text: "",
											by: ["engine", {}],
										});
										is_set.set_text("");
										is_set.properties.by.ensure("engine", {});
									},
								});
								notifyEntityUpdate(this.property.parent);
							},
							yes: ($) => {
								this.update($.properties.value, "engine");
							},
						});
					},
				});
			},
		});
	}

	__server_value(value: string) {
		if (isPasswordProperty(this.property.inferences.text_type())) return;
		this.property.root.transact(() => {
			this.property.properties.backend_known
				.ensure("yes", {
					value: value,
				})
				.set_value(value);
			this.property.properties.is_editable.switch({
				no: () => this.__update(value, "engine"),
				yes: (editable) =>
					editable.properties.modified.switch({
						no: () => this.__update(value, "engine"),
						yes: ($) => {
							if ($.properties.value === value) {
								editable.properties.modified.set("no", {});
							}
						},
					}),
			});
			this.property.properties.navigable.switch({
				no: () => undefined,
				yes: ($) => iReference($).updateKeyInServerValue(value),
			});
		});
	}

	setServerValue(value: string) {
		this.__server_value(value);
		this.graph?.setServerEdgeValue(this.entry!, value);
		for (const graph of this._graphs_for_which_is_key()) {
			graph.updateServerKeyValue(this.entry!, value);
		}
		if (this.references_to_me) {
			for (const reference of this.references_to_me) {
				if (reference.propagateKeyUpdates) {
					iText(reference.reference.parent).__ensure(value);
				}
			}
		}
	}

	updatePermissions(permitted: "yes" | "no") {
		switch (this.property.inferences.text_type().properties.type.state.name) {
			case "derived":
				break;
			case "base":
				switch (permitted) {
					case "no":
						this.property.properties.is_editable.ensure("no", {});
						break;
					case "yes":
						this.property.properties.is_editable.ensure("yes", {
							modified: ["no", {}],
							"edit mode": nodeEditMode(this.property.component_root),
						});
				}
		}
	}

	setR2M(reference: ReferenceInterface, destructor: DestructorTree) {
		(this.references_to_me ??= new Set()).add(reference);
		destructor.onDestroy(() => this.references_to_me?.delete(reference));
	}
}

export type iText = TextInterface;
export const iText = WeakCache<api_cs.Ctext__type__property, TextInterface>((s) => new TextInterface(s));
